/**
 * **What every panel write has in common** — the envelope, the route, the key and the confirm
 * (issue #310).
 *
 * There were two copies of this by #303 and this issue would have made three: `memory.ts` and
 * `roster.ts` each carried their own `familyPath`, their own `newIdempotencyKey`, their own
 * spelling of the tools' shared envelope and their own `confirm*Write`. Identical, and drifting
 * was only a matter of time — which is the argument `contextNouns.ts` already won inside the
 * service and `words.ts` won inside this app.
 *
 * **Nothing about a *particular* write lives here**, and that boundary is the reason this is
 * safe to share: a tool's own envelope extensions (`RememberEnvelope.collision`,
 * `ForgetEnvelope.deleting`, `DeleteItemEnvelope.deleting`, `DisbandEnvelope.erased`) stay in
 * the module that renders them, and every propose helper stays with its screen. What is here is
 * the plumbing all twelve routes share and none of them may spell differently.
 */

import { STEP_UP_BODY_FIELD } from "@pilotfish/core";
import { type AppApi, appFetch } from "./client.ts";

/**
 * The routes the panel writes through — #298's `WRITE_ROUTES`, minus the ones no screen uses.
 *
 * A union rather than a bare string, so {@link confirmWrite} cannot be handed a tail that names
 * no tool. That matters because `confirmProposal`'s `expectedTool` (#107) refuses a proposal
 * confirmed through the wrong route, and a mis-typed tail would turn *"yes, delete it"* into a
 * refusal the family cannot act on.
 *
 * **`/capture` is deliberately absent, and its absence is a product rule** (#302, #310). The
 * panel has no *＋ New* on either the memory screen or the list, because classifying an
 * utterance is the host model's job and the server calls no completions API — so a panel could
 * only create by making the family fill in a form, which is the thing this product exists not
 * to be. `/capture` is the route that would do it, so it is not in the vocabulary this app can
 * express. `send_feedback` is absent for #298's own reason: it is the only tool that sends
 * family language outside the tenant, and whether the panel offers that is a product question
 * nobody has asked yet.
 */
export type WriteTail =
  | "/resolve"
  | "/update"
  | "/remember"
  | "/correct-this"
  | "/forget-this"
  | "/delete-item"
  | "/invite-member"
  | "/revoke-invitation"
  | "/remove-member"
  | "/leave-family"
  | "/transfer-ownership"
  | "/disband-family";

/**
 * What a write answers with — the tools' shared envelope (ADR 0006 decision 5).
 *
 * `status` is the field to branch on, never the HTTP code: #298 answers `200` for both, on the
 * grounds that the envelope's own status is the structural statement and a `202` would be a
 * second, weaker one.
 */
export interface WriteEnvelope {
  readonly status: "applied" | "proposed";
  readonly needsConfirmation: boolean;
  readonly proposalId: string | null;
  /** The family-facing sentence. **The screen relays this and composes no alternative.** */
  readonly message: string;
}

export function familyPath(familyId: string, tail: string): string {
  return `/app/families/${encodeURIComponent(familyId)}${tail}`;
}

/**
 * A fresh idempotency key.
 *
 * `crypto.randomUUID()` is available in every browser this app supports and its output is
 * inside the tool contract's `[A-Za-z0-9._:-]+` (#119). It is minted **per attempt** rather
 * than per keystroke: the guard exists so a retry of *the same* write is absorbed, and a key
 * held across an edited body would make the second, different write a silent replay of the
 * first.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** One propose, on the tool's own route, carrying the tool's own input. */
export function postWrite<T extends WriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: WriteTail,
  body: Record<string, unknown>,
): Promise<T> {
  return appFetch<T>(api, familyPath(familyId, tail), { method: "POST", body });
}

/**
 * Apply a stored proposal: `{ confirm: true, proposalId }` and **nothing else**.
 *
 * The body-free confirm is ADR 0011's whole mechanism, so this function takes no payload to
 * send — a paraphrased re-send cannot change what is written because there is nowhere to put
 * one. `tail` names the tool, which is what lets `confirmProposal`'s `expectedTool` (#107)
 * refuse a `forget` proposal confirmed through `/remember`.
 */
export function confirmWrite<T extends WriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: WriteTail,
  proposalId: string,
  stepUp?: StepUpProof,
): Promise<T> {
  return postWrite<T>(api, familyId, tail, {
    confirm: true,
    proposalId,
    ...(stepUp === undefined ? {} : { [STEP_UP_BODY_FIELD]: stepUp }),
  });
}

/**
 * **The second factor a covered write carries** (issue #307, ADR 0021 decision 8a).
 *
 * The one exception to the sentence above it, and it is not a loophole in the same rule but the
 * other side of it: ADR 0011 keeps the *payload* server-side so a confirm cannot change what is
 * written, and this changes nothing about the write — it says who is at the keyboard. It cannot
 * name a different proposal, a different tool or a different family; the write is still the
 * stored one.
 *
 * Which writes carry it is the **server's** decision and never this app's: a client that
 * skipped the screen would simply be refused, so the gate is not the screen
 * (`services/api/src/app/writes.ts`). The panel sends this only because it has just been told
 * to, by a `step_up_required` refusal.
 */
export interface StepUpProof {
  readonly methodId: string;
  readonly code: string;
}

/**
 * **Ask for a code** — `POST /app/me/step-up` (issue #307).
 *
 * A person-scope route, so it takes no family and no target: a session belongs to a person, and
 * account deletion (#382) has no family to hang a challenge off. The address is resolved
 * server-side from the session and is deliberately not a parameter here — an endpoint that took
 * one would mail a second factor wherever a valid session asked it to.
 *
 * Calling it again is how *"Didn't arrive? Send another"* works: each send mints a fresh
 * `methodId`, and the proof the confirm carries is whichever one the family typed.
 */
export function requestStepUpCode(api: AppApi): Promise<StepUpChallenge> {
  return appFetch<StepUpChallenge>(api, "/app/me/step-up", { method: "POST", body: {} });
}

/** What a send answers with — the server's own `MePayloads["POST /step-up"]` shape. */
export interface StepUpChallenge {
  readonly methodId: string;
  /** The caller's own verified address, so the screen can say which inbox to look in. */
  readonly sentTo: string;
  readonly expiresInMinutes: number;
}
